import { http } from '@/lib/ats-https';
import { z } from 'zod';
import { AiScoringSchemas } from './aiScoring.schema';

// Rejection-email config sent under `ai_scoring_attributes` (ENG-3114). Keys
// match the backend permit; all optional so the existing 3-field auto-reject
// callers (enabled/auto_reject/threshold) are unaffected.
export interface AutoRejectEmailSettings {
  auto_reject_send_email?: boolean;
  // The user-selected source template; the backend clones it into a private
  // send template that carries the (editable) subject/body below.
  source_ats_comm_template_id?: number | null;
  auto_reject_email_subject?: string | null;
  auto_reject_email_body?: string | null;
  auto_reject_sender_id?: number | null;
  auto_reject_ats_shared_email_id?: number | null;
  auto_reject_schedule?: boolean;
  auto_reject_send_after?: number | null;
  auto_reject_scheduled_at?: string | null;
  auto_reject_scheduled_at_timezone?: string | null;
  auto_reject_cc?: string[];
  auto_reject_bcc?: string[];
}

const BASE = '/ats_jobs';

// GET /ats_jobs/{ats_job_id}/ai_scoring - Request Schema
const FetchAiScoringRequestSchema = z.object({
  ats_job_id: z.union([z.string(), z.number()]).transform(String), // Accept both string and number, convert to string
});

// Update schema to match actual API response structure
const AiScoringResponseSchema = z.object({
  data: z.object({
    ai_scoring: z
      .object({
        id: z.union([z.string(), z.number()]),
        enabled: z.boolean(),
        auto_reject: z.boolean(),
        auto_reject_threshold: z.number().int().nullable().default(75),
        // Rejection-email config (ENG-3114). The GET exposes the user-selected
        // *source* template as `source_ats_comm_template_id` (not the private
        // send-clone), so keep that key here — Zod strips anything unlisted,
        // which is what previously dropped the value on restore-on-edit.
        auto_reject_send_email: z.boolean().optional(),
        source_ats_comm_template_id: z
          .union([z.string(), z.number()])
          .nullable()
          .optional(),
        options: z
          .object({
            auto_reject_schedule: z
              .union([z.boolean(), z.string(), z.number()])
              .nullable()
              .optional(),
            auto_reject_send_after: z
              .union([z.number(), z.string()])
              .nullable()
              .optional(),
            auto_reject_scheduled_at: z.string().nullable().optional(),
            auto_reject_scheduled_at_timezone: z.string().nullable().optional(),
            auto_reject_ats_shared_email_id: z
              .union([z.number(), z.string()])
              .nullable()
              .optional(),
            auto_reject_sender_id: z
              .union([z.number(), z.string()])
              .nullable()
              .optional(),
            auto_reject_email_subject: z.string().nullable().optional(),
            auto_reject_email_body: z.string().nullable().optional(),
            auto_reject_cc: z.array(z.string()).nullable().optional(),
            auto_reject_bcc: z.array(z.string()).nullable().optional(),
          })
          .nullable()
          .optional(),
        icp: z.string().nullable(),
        categories: z.array(z.string()).optional(),
        attributes: z
          .array(AiScoringSchemas.AiScoringAttributeSchema)
          .optional()
          .default([]),
        version: z.string().nullable().optional(),
      })
      .optional(),
    bulk_review_progress: z
      .object({
        status: z.string().optional(),
        failed: z.number().optional(),
        success: z.number().optional(),
        processed: z.number().optional(),
        total: z.number().optional(),
      })
      .optional(),
  }),
  errors: z.array(z.any()),
});

export type aiScoringResponseType = z.infer<typeof AiScoringResponseSchema>;

async function fetchAiScoring(
  atsJobId: string | number
): Promise<{ data?: z.infer<typeof AiScoringResponseSchema> }> {
  try {
    // Validate request parameters - this will convert number to string if needed
    const validatedRequest = FetchAiScoringRequestSchema.parse({
      ats_job_id: atsJobId,
    });
    const jobId = validatedRequest.ats_job_id;

    const response = await http.get(`${BASE}/${jobId}/ai_scoring`);

    // The actual API response has { data: {...}, errors: [] } structure
    // Validate the full response structure with flexible schema

    try {
      const validatedResponse = AiScoringResponseSchema.parse(response);
      return { data: validatedResponse };
    } catch {
      // Response failed schema validation — return an empty result rather than
      // falling through to `undefined`, which would make React Query throw
      // "Query data cannot be undefined" for every consumer of this queryFn.
      return {};
    }
  } catch (error) {
    return { data: { errors: [error as Error] } };
  }
}

const _AiFeedbackSchema = z.object({
  rating: z.union([z.literal(0), z.literal(1)]), // Only 0 or 1 allowed
  feedback: z.string().optional(),
  trace_id: z.string().optional().nullable(),
  feedbackable_id: z.number().optional(),
  feedbackable_type: z.string().optional(),
  meta: z
    .object({
      feature: z.string().optional(),
      ats_job_id: z.number().optional(),
      is_test_run: z.union([z.string(), z.boolean()]).optional(),
    })
    .optional(),
});

async function aiFeedback(data: z.infer<typeof _AiFeedbackSchema>) {
  try {
    http.post(`/ai_feedback`, {
      ai_feedback: {
        feedback: data.feedback,
        rating: data.rating,
        trace_id: data.trace_id ?? '',
        // feedbackable_id: data.feedbackable_id,
        // feedbackable_type: data.feedbackable_type,
        meta: data.meta,
      },
    });
  } catch (error) {
    return { data: { errors: [error as Error] } };
  }
}

const getApplicantAiScoreResponseSchema = z.object({
  data: z.object({
    application_id: z.number(),
    ai_candidate_review: z.object({
      id: z.number(),
      status: z.string(),
      trace_id: z.string().optional().nullable(),
      rating: z.number(),
      score_level: z.string().nullable(),
      evaluation: z.string().nullable(),
      reasoning: z.string().nullable(),
      attributes: z.array(AiScoringSchemas.AiScoringAttributeSchema),
      attribute_scores: z.array(
        z.object({
          attribute_id: z.number(),
          label: z.string(),
          score_level: z.string(),
          score: z.union([z.number(), z.string()]).transform((val) => {
            if (typeof val === 'string') {
              const num = parseFloat(val);
              return isNaN(num) ? 0 : num;
            }
            return val;
          }),
          reasoning: z.string(),
        })
      ),
    }),
  }),
  errors: z.array(z.any()),
});

const getApplicantAiScore = async (
  atsJobId: string,
  applicantId: string
): Promise<{
  data?: z.infer<typeof getApplicantAiScoreResponseSchema>;
  error?: Error;
}> => {
  try {
    const response = await http.get(
      `${BASE}/${atsJobId}/ai_scoring/applicant/${applicantId}`
    );
    try {
      const data = getApplicantAiScoreResponseSchema.parse(response);
      return { data };
    } catch (error) {
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
};

async function updateAiScoringSettings(
  atsJobId: string | number,
  settings: {
    enabled: boolean;
    auto_reject: boolean;
    auto_reject_threshold: number;
  } & AutoRejectEmailSettings
) {
  return http.put(`${BASE}/${atsJobId}`, {
    ai_scoring_attributes: settings,
  });
}

export const aiScoringApi = {
  fetchAiScoring,
  aiFeedback,
  getApplicantAiScore,
  updateAiScoringSettings,
};
